import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Video, BookOpen, Award, Book, Wrench, Mic, Clock, Users,
  Calendar, Upload, FileText, CheckCircle, ChevronRight,
  Play, Settings, Bell, HelpCircle, Star, Plus, X, Loader2,
  FileQuestion
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = [
  {
    key: 'webinar',
    name: 'Webinar',
    icon: Video,
    color: 'bg-green-500',
    description: 'Sesión en vivo única sobre un tema específico. Perfecto para actualizaciones y novedades.',
    duration: '1-2 horas',
    sessions: '1 sesión',
    price: '$500 - $2,000 MXN',
    isPaid: true,
    includes: ['Sesión en vivo', 'Q&A en tiempo real', 'Grabación disponible', 'Material de apoyo']
  },
  {
    key: 'masterclass',
    name: 'Master Class',
    icon: Award,
    color: 'bg-purple-500',
    description: 'Sesión GRATUITA de inmersión profunda. Contenido de regalo para usuarios gratuitos como muestra de nuestro valor.',
    duration: '2-4 horas',
    sessions: '1-3 sesiones',
    price: 'GRATIS',
    isPaid: false,
    badge: '🎁 REGALO',
    includes: ['Experto de alto nivel', 'Casos de estudio reales', 'Material exclusivo', 'Acceso gratuito para todos']
  },
  {
    key: 'taller',
    name: 'Taller',
    icon: Wrench,
    color: 'bg-orange-500',
    description: 'Sesión práctica intensiva donde aprenderás haciendo. Enfocado en desarrollar habilidades específicas.',
    duration: '3-6 horas',
    sessions: '1-2 sesiones',
    price: '$800 - $3,000 MXN',
    isPaid: true,
    includes: ['Práctica en vivo', 'Ejercicios guiados', 'Retroalimentación inmediata', 'Materiales de trabajo']
  },
  {
    key: 'ponencia',
    name: 'Ponencia',
    icon: Mic,
    color: 'bg-blue-500',
    description: 'Presentación magistral de un experto sobre un tema especializado. Perspectivas únicas y tendencias.',
    duration: '45-90 minutos',
    sessions: '1 sesión',
    price: '$300 - $1,000 MXN',
    isPaid: true,
    includes: ['Presentación de experto', 'Insights exclusivos', 'Sesión de preguntas', 'Grabación disponible']
  },
  {
    key: 'curso',
    name: 'Curso',
    icon: BookOpen,
    color: 'bg-blue-600',
    description: 'Programa educativo completo con múltiples sesiones. Ideal para aprendizaje estructurado y progresivo.',
    duration: '4-12 semanas',
    sessions: '8-24 sesiones',
    price: '$2,000 - $15,000 MXN',
    isPaid: true,
    includes: ['Módulos estructurados', 'Material descargable', 'Ejercicios prácticos', 'Certificado de finalización']
  },
  {
    key: 'diplomado',
    name: 'Diplomado',
    icon: Book,
    color: 'bg-red-600',
    description: 'Programa de certificación profesional completo con evaluaciones y certificación oficial UPIM.',
    duration: '3-6 meses',
    sessions: '20-50 sesiones',
    price: '$15,000 - $50,000 MXN',
    isPaid: true,
    includes: ['Plan de estudios completo', 'Evaluaciones', 'Proyecto final', 'Certificación oficial UPIM']
  }
];

export default function SpeakerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [myContent, setMyContent] = useState([]);
  const [stats, setStats] = useState({ total: 0, webinars: 0, views: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    short_description: '',
    content_type: 'webinar',
    category: 'Ventas y Negociación',
    level: 'Intermedio',
    duration_minutes: 90,
    scheduled_start: '',
    is_free: false,
    price: 0,
    has_certificate: true
  });

  const token = localStorage.getItem('upim_token');
  const user = token ? JSON.parse(localStorage.getItem('upim_user') || '{}') : null;

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, token]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load speaker profile
      const profileRes = await axios.get(`${API_URL}/api/content/speakers/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(profileRes.data.profile);
      
      // Load my content
      const contentRes = await axios.get(`${API_URL}/api/educational/my-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyContent(contentRes.data.items || []);
      
      // Calculate stats
      const items = contentRes.data.items || [];
      setStats({
        total: items.length,
        webinars: items.filter(i => i.content_type === 'webinar').length,
        views: items.reduce((acc, i) => acc + (i.total_registrations || 0), 0)
      });
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContent = async () => {
    if (!newContent.title || newContent.title.length < 5) {
      setCreateError('El título debe tener al menos 5 caracteres');
      return;
    }

    try {
      setCreating(true);
      setCreateError('');

      // Master Classes are always free
      const payload = {
        ...newContent,
        is_free: newContent.content_type === 'masterclass' ? true : newContent.is_free,
        price: newContent.content_type === 'masterclass' ? 0 : newContent.price
      };

      await axios.post(`${API_URL}/api/educational/content`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreateSuccess('¡Contenido creado exitosamente! Está pendiente de aprobación por el administrador.');
      setShowCreateModal(false);
      setNewContent({
        title: '',
        description: '',
        short_description: '',
        content_type: 'webinar',
        category: 'Ventas y Negociación',
        level: 'Intermedio',
        duration_minutes: 90,
        scheduled_start: '',
        is_free: false,
        price: 0,
        has_certificate: true
      });
      loadData();

      setTimeout(() => setCreateSuccess(''), 5000);
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Error al crear contenido');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="speaker-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Portal de Ponentes</h1>
              <p className="text-purple-200">Bienvenido, {user?.name || 'Ponente'}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/examenes')}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <FileQuestion className="w-4 h-4" />
                Gestionar Exámenes
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Crear Contenido
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Success/Error Messages */}
        {createSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800">{createSuccess}</span>
          </div>
        )}
        {createError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <X className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{createError}</span>
            <button onClick={() => setCreateError('')} className="ml-auto text-red-600 hover:text-red-800">✕</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Video className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-gray-500">Contenidos Creados</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats.views}</p>
                <p className="text-gray-500">Total Inscripciones</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{profile?.rating?.toFixed(1) || '5.0'}</p>
                <p className="text-gray-500">Calificación Promedio</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Types Guide */}
        <div className="bg-white rounded-xl shadow-sm mb-8">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-6 h-6 text-indigo-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Tipos de Contenido Educativo</h2>
                <p className="text-gray-500">Conoce las diferencias para elegir el formato correcto</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CONTENT_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <div key={type.key} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${type.color} rounded-lg shrink-0`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900">{type.name}</h3>
                        {type.badge && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            {type.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span><strong>Duración:</strong> {type.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span><strong>Sesiones:</strong> {type.sessions}</span>
                        </div>
                        <div className={`flex items-center gap-2 ${type.isPaid ? 'text-gray-700' : 'text-green-600 font-bold'}`}>
                          <span>💰</span>
                          <span>{type.price}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-700 mb-2">Incluye:</p>
                        <ul className="space-y-1">
                          {type.includes.map((inc, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                              {inc}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 mb-8 border border-purple-200">
          <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notas Importantes para Ponentes
          </h3>
          <ul className="space-y-2 text-purple-800">
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-1">•</span>
              <span><strong>Master Classes son siempre GRATIS</strong> - Son nuestro contenido de regalo para atraer usuarios.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-1">•</span>
              <span><strong>Sube tus materiales 24 horas antes</strong> del evento para que los participantes puedan prepararlos.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-1">•</span>
              <span><strong>Las transmisiones se graban automáticamente</strong> y se suben a la biblioteca de videos.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-1">•</span>
              <span><strong>Conéctate 15 minutos antes</strong> para probar tu audio y video.</span>
            </li>
          </ul>
        </div>

        {/* My Content */}
        {myContent.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Mi Contenido</h2>
              <button
                onClick={() => navigate('/admin/academia')}
                className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              >
                Ver todos <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {myContent.slice(0, 5).map(item => (
                <div key={item.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                  <div className={`p-2 rounded-lg ${
                    item.content_type === 'masterclass' ? 'bg-purple-100' :
                    item.content_type === 'webinar' ? 'bg-green-100' :
                    item.content_type === 'taller' ? 'bg-orange-100' :
                    item.content_type === 'curso' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Video className={`w-5 h-5 ${
                      item.content_type === 'masterclass' ? 'text-purple-600' :
                      item.content_type === 'webinar' ? 'text-green-600' :
                      item.content_type === 'taller' ? 'text-orange-600' :
                      item.content_type === 'curso' ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500 capitalize">{item.content_type} • {item.status}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{item.total_registrations || 0} inscritos</p>
                    {item.is_free ? (
                      <span className="text-green-600 text-sm">Gratis</span>
                    ) : (
                      <span className="text-gray-500 text-sm">${item.price?.toLocaleString()} MXN</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Content Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Crear Nuevo Contenido</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Contenido *</label>
                <select
                  value={newContent.content_type}
                  onChange={(e) => setNewContent({ ...newContent, content_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="webinar">Webinar ($500 - $2,000)</option>
                  <option value="masterclass">Master Class (GRATIS)</option>
                  <option value="taller">Taller ($800 - $3,000)</option>
                  <option value="ponencia">Ponencia ($300 - $1,000)</option>
                  <option value="curso">Curso ($2,000 - $15,000)</option>
                  <option value="diplomado">Diplomado ($15,000 - $50,000)</option>
                </select>
                {newContent.content_type === 'masterclass' && (
                  <p className="text-sm text-green-600 mt-1">Las Master Classes son siempre GRATIS</p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                <input
                  type="text"
                  value={newContent.title}
                  onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Ej: Técnicas de Cierre de Ventas Inmobiliarias"
                />
              </div>

              {/* Short Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción Corta</label>
                <input
                  type="text"
                  value={newContent.short_description}
                  onChange={(e) => setNewContent({ ...newContent, short_description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Una frase que describa tu contenido"
                  maxLength={200}
                />
              </div>

              {/* Full Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción Completa</label>
                <textarea
                  value={newContent.description}
                  onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Describe de qué tratará tu contenido, qué aprenderán los participantes..."
                />
              </div>

              {/* Category & Level */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
                  <select
                    value={newContent.category}
                    onChange={(e) => setNewContent({ ...newContent, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Legal Inmobiliario">Legal Inmobiliario</option>
                    <option value="Fiscal y Contable">Fiscal y Contable</option>
                    <option value="Ventas y Negociación">Ventas y Negociación</option>
                    <option value="Marketing Digital">Marketing Digital</option>
                    <option value="Administración de Propiedades">Administración de Propiedades</option>
                    <option value="Valuación">Valuación</option>
                    <option value="Desarrollo Inmobiliario">Desarrollo Inmobiliario</option>
                    <option value="Inversiones">Inversiones</option>
                    <option value="Tecnología">Tecnología</option>
                    <option value="Ética Profesional">Ética Profesional</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nivel</label>
                  <select
                    value={newContent.level}
                    onChange={(e) => setNewContent({ ...newContent, level: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Principiante">Principiante</option>
                    <option value="Intermedio">Intermedio</option>
                    <option value="Avanzado">Avanzado</option>
                    <option value="Experto">Experto</option>
                  </select>
                </div>
              </div>

              {/* Duration & Schedule */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duración (minutos)</label>
                  <input
                    type="number"
                    value={newContent.duration_minutes}
                    onChange={(e) => setNewContent({ ...newContent, duration_minutes: parseInt(e.target.value) || 60 })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min={15}
                    max={480}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha y Hora (opcional)</label>
                  <input
                    type="datetime-local"
                    value={newContent.scheduled_start}
                    onChange={(e) => setNewContent({ ...newContent, scheduled_start: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Price (only for paid content) */}
              {newContent.content_type !== 'masterclass' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newContent.is_free}
                        onChange={(e) => setNewContent({ ...newContent, is_free: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">¿Es contenido gratuito?</span>
                    </label>
                  </div>
                  {!newContent.is_free && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Precio (MXN)</label>
                      <input
                        type="number"
                        value={newContent.price}
                        onChange={(e) => setNewContent({ ...newContent, price: parseFloat(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        min={0}
                        step={100}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Certificate */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newContent.has_certificate}
                    onChange={(e) => setNewContent({ ...newContent, has_certificate: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Incluir certificado de participación</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateContent}
                  disabled={creating}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Crear Contenido
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
